import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { Card, CopyField, EmptyState, TabHeading, UpgradeCard } from "./StudioShared";

export default function LiveTab({ id, locked }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState(null);
  const [streamers, setStreamers] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [adding, setAdding] = useState(false);
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newPassword, setNewPassword] = useState(null); // {username, password} montré une seule fois
  const [pwCopied, setPwCopied] = useState(false);

  const loadStreamers = useCallback(async () => {
    try {
      const r = await api.get(`/api/radios/${id}/streamers`);
      setStreamers(Array.isArray(r.data) ? r.data : []);
    } catch { setStreamers([]); }
  }, [id]);

  useEffect(() => {
    if (locked) { setLoading(false); return; }
    let mounted = true;
    (async () => {
      await Promise.all([
        api.get(`/api/radios/${id}/live-info`).then((r) => { if (mounted) setInfo(r.data); }).catch(() => {}),
        loadStreamers(),
        api.get(`/api/radios/${id}/broadcasts`).then((r) => { if (mounted) setBroadcasts(Array.isArray(r.data) ? r.data : []); }).catch(() => {}),
      ]);
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [id, locked, loadStreamers]);

  if (locked) {
    return <UpgradeCard title={t("studio.locked.liveTitle")} desc={t("studio.locked.liveDesc")} />;
  }

  const createStreamer = async () => {
    if (!username.trim()) return;
    setCreating(true);
    try {
      const r = await api.post(`/api/radios/${id}/streamers`, {
        streamer_username: username.trim(),
        display_name: displayName.trim() || username.trim(),
      });
      const pw = r.data?.streamer_password;
      if (pw) setNewPassword({ username: username.trim(), password: pw });
      setUsername("");
      setDisplayName("");
      setAdding(false);
      await loadStreamers();
    } catch { /* ignore */ } finally {
      setCreating(false);
    }
  };

  const removeStreamer = async (sid) => {
    if (!window.confirm(t("live.confirmDeleteDj"))) return;
    try {
      await api.delete(`/api/radios/${id}/streamers/${sid}`);
      await loadStreamers();
    } catch { /* ignore */ }
  };

  const copyPw = () => {
    if (!newPassword) return;
    navigator.clipboard.writeText(newPassword.password).then(() => {
      setPwCopied(true);
      setTimeout(() => setPwCopied(false), 1200);
    });
  };

  if (loading) return <p className="text-gray-500">{t("studio.loading")}</p>;

  return (
    <div className="space-y-6">
      <TabHeading
        title={t("live.title")}
        subtitle={t("live.subtitle")}
        action={
          info?.webdj_url && (
            <a
              href={info.webdj_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md"
            >
              🎙️ {t("live.webDj")}
            </a>
          )
        }
      />
      {info?.webdj_url && <p className="-mt-3 text-xs text-gray-400">{t("live.webDjNote")}</p>}

      {/* Réglages de connexion (logiciel de diffusion) */}
      <Card>
        <h3 className="font-semibold text-gray-900">{t("live.connectTitle")}</h3>
        <p className="text-sm text-gray-500 mt-1">{t("live.connectDesc")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <CopyField label={t("live.host")} value={info?.host} />
          <CopyField label={t("live.port")} value={info?.dj_port} />
          <CopyField label={t("live.mount")} value={info?.mount} />
          <CopyField label={t("live.format")} value={info?.format} />
          {info?.protocol && <CopyField label={t("live.protocol")} value={info.protocol} />}
        </div>
        <p className="text-xs text-gray-400 mt-3">{t("live.credentialsNote")}</p>
      </Card>

      {/* Mot de passe DJ affiché une seule fois */}
      {newPassword && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-amber-900">{t("live.passwordOnce")}</h3>
              <p className="text-sm text-amber-800 mt-1">{t("live.passwordWarning")}</p>
            </div>
            <button onClick={() => setNewPassword(null)} className="text-amber-700 hover:text-amber-900 text-lg leading-none">✕</button>
          </div>
          <div className="mt-3 flex items-center gap-2 bg-white rounded-xl border border-amber-200 p-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500">{newPassword.username}</p>
              <code className="text-sm text-gray-900 break-all">{newPassword.password}</code>
            </div>
            <button onClick={copyPw} className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-md whitespace-nowrap">
              {pwCopied ? t("studio.actions.copied") : t("studio.actions.copy")}
            </button>
          </div>
        </Card>
      )}

      {/* Comptes DJ */}
      <Card>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3 className="font-semibold text-gray-900">{t("live.djAccounts")}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{t("live.djAccountsDesc")}</p>
          </div>
          <button onClick={() => setAdding((a) => !a)} className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md">
            ＋ {t("live.addDj")}
          </button>
        </div>

        {adding && (
          <div className="flex flex-wrap items-end gap-3 mb-4 rounded-xl border border-gray-100 p-4 bg-gray-50/50">
            <label className="text-xs text-gray-500">
              <span className="block mb-1">{t("live.username")}</span>
              <input value={username} onChange={(e) => setUsername(e.target.value)} className="border border-gray-200 rounded-md px-3 py-2 text-sm" />
            </label>
            <label className="text-xs text-gray-500">
              <span className="block mb-1">{t("live.displayName")}</span>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="border border-gray-200 rounded-md px-3 py-2 text-sm" />
            </label>
            <button onClick={createStreamer} disabled={creating || !username.trim()} className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md disabled:opacity-50">
              {t("live.createDj")}
            </button>
            <button onClick={() => setAdding(false)} className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md">
              {t("studio.actions.cancel")}
            </button>
          </div>
        )}

        {streamers.length === 0 ? (
          <p className="text-sm text-gray-500">{t("live.noDjs")}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {streamers.map((s) => (
              <li key={s.id} className="flex items-center gap-3 py-3">
                <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">🎧</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{s.display_name || s.streamer_username}</div>
                  <div className="text-xs text-gray-500 truncate">{s.streamer_username}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${s.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {s.is_active ? t("live.active") : t("live.inactive")}
                </span>
                <button onClick={() => removeStreamer(s.id)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 flex items-center justify-center">✕</button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Diffusions passées */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">{t("live.broadcasts")}</h3>
        {broadcasts.length === 0 ? (
          <EmptyState icon="📡" title={t("live.noBroadcasts")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {broadcasts.map((b, i) => (
              <li key={b.id || i} className="flex items-center gap-3 py-3 text-sm">
                <span className="flex-1 min-w-0 truncate text-gray-900">{b.streamer_name || b.streamer || t("live.streamer")}</span>
                <span className="text-gray-500">{b.start ? new Date(b.start).toLocaleString() : (b.timestampStart ? new Date(b.timestampStart * 1000).toLocaleString() : "—")}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
